import React, { useEffect, useRef } from 'react';

function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

export default function ParticleBackground({ height = 480 }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animationId;

        function resize() {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        }
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        const COLORS = ['#969e57', '#cebc9d', '#4e4a4d', '#fe2b57', '#ff8f78', '#e7b621'];
        const NUM = 72;
        const particles = Array.from({ length: NUM }, () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.55,
            vy: (Math.random() - 0.5) * 0.55,
            r: Math.random() * 2.2 + 1.0,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            alpha: Math.random() * 0.45 + 0.25,
            pulse: Math.random() * Math.PI * 2,
            pulseSpeed: Math.random() * 0.025 + 0.008,
        }));

        function draw() {
            const w = canvas.width;
            const h = canvas.height;
            ctx.clearRect(0, 0, w, h);

            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 130) {
                        const op = (1 - dist / 130) * 0.28;
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(150,158,87,${op})`;
                        ctx.lineWidth = 0.7;
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.stroke();
                    }
                }
            }

            particles.forEach(p => {
                p.pulse += p.pulseSpeed;
                const pr = p.r + Math.sin(p.pulse) * 0.7;
                const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 4);
                grad.addColorStop(0, hexToRgba(p.color, p.alpha * 0.8));
                grad.addColorStop(1, hexToRgba(p.color, 0));
                ctx.beginPath();
                ctx.arc(p.x, p.y, pr * 4, 0, Math.PI * 2);
                ctx.fillStyle = grad;
                ctx.fill();

                ctx.beginPath();
                ctx.arc(p.x, p.y, pr, 0, Math.PI * 2);
                ctx.fillStyle = hexToRgba(p.color, p.alpha);
                ctx.fill();

                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > w) p.vx *= -1;
                if (p.y < 0 || p.y > h) p.vy *= -1;
            });

            animationId = requestAnimationFrame(draw);
        }

        draw();
        return () => { cancelAnimationFrame(animationId); ro.disconnect(); };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: height,
                pointerEvents: 'none', zIndex: 0, display: 'block',
            }}
        />
    );
}
